// src/pages/patients/PatientECGReports.jsx
import React, { useContext, useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { AuthContext } from "../../context/AuthContextProvider.js";
import moment from "moment";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
// removed react-otp-input import (custom implementation below)
import Timer from "otp-timer";
import { useGetEcgList, getEcgReportAction } from "../../api/EcgReports";
import nopreview from "../../assets/images/others/no-preview.png";
import TypeIcon from "../../components/Reusables/HubSpokeIcon/TypeIcon.jsx";

/**
 * PatientECGReports — MUI Table version
 * Props:
 *  - patient
 *  - setReportToken
 *  - reportToken
 *  - onCountsChange (optional)  // { localEcgCount, otherReportsCount }
 */
const PatientECGReports = ({ patient, setReportToken, reportToken, onCountsChange }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // Consent modal / OTP state
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [sendOTP, setSendOTP] = useState(false);
  const [otp, setOtp] = useState("");
  const [sendOtpLoading, setSendOtpLoading] = useState(false);
  const [verifyOtpLoading, setVerifyOtpLoading] = useState(false);

  // token & args state
  const [patientSessionToken, setPatientSessionToken] = useState(reportToken || null);
  const [args, setArgs] = useState({});

  // useEffect(() => {
  //   const base = { patientId: patient?.patientId || patient?.id };

  //   if (reportToken) base.reportToken = reportToken;
  //   if (patientSessionToken) {
  //     base.patientSessionToken = patientSessionToken;
  //     base.viewOtherReport = true;
  //   }

  //   setArgs(base);
  // }, [patient?.patientId, patient?.id, reportToken, patientSessionToken]);


  // useEffect(() => {
  //   const base = { patientId: patient?.patientId || patient?.id };
  //   if (reportToken) base.reportToken = reportToken;
  //   if (patientSessionToken) {
  //     base.patientSessionToken = patientSessionToken;
  //     base.viewOtherReport = true;
  //   }
  //   setArgs(base);
  // }, [patient?.patientId, reportToken, patientSessionToken]);

  useEffect(() => {
  if (!patient?.patientId && !patient?.id) return;

  const base = { patientId: patient.patientId || patient.id };

  if (reportToken) base.reportToken = reportToken;
  if (patientSessionToken) {
    base.patientSessionToken = patientSessionToken;
    base.viewOtherReport = true;
  }

  setArgs(base);
}, [patient?.patientId, patient?.id, reportToken, patientSessionToken]);


  const { ecgReport: ecgReports = [], data = {}, isLoading: isEcgLoading } = useGetEcgList(args);

  // separate counts for clarity
  const localEcgCount = Array.isArray(ecgReports) ? ecgReports.length : 0;
  // Always read count from API response (even if otherReports array empty)
  useEffect(() => {
    if (!isEcgLoading) {
      onCountsChange?.({ localEcgCount });
    }
  }, [localEcgCount, isEcgLoading, onCountsChange]);


  const patientPhone = patient?.phoneNumber || "";
  const patientIdQuery = patient?.patientId;

  const handleOpenConsent = () => {
    setShowConsentModal(true);
    setSendOTP(false);
    setOtp("");
  };

  const handleCloseConsent = () => {
    setShowConsentModal(false);
    setSendOTP(false);
    setOtp("");
  };

  const handleSendOtp = useCallback(async () => {
    if (!patientIdQuery) return;

    try {
      setSendOtpLoading(true);

      const res = await getEcgReportAction({
        patientId: patientIdQuery,
        viewOtherReport: true,
        sendOtp: true, //  explicit intent
      });

      if (res?.success || res?.otpSent) {
        setSendOTP(true); //  only after success
      } else {
        console.error("OTP not sent", res);
      }
    } catch (err) {
      console.error("send OTP failed", err);
    } finally {
      setSendOtpLoading(false);
    }
  }, [patientIdQuery]);


  const handleVerifyOtp = useCallback(async () => {
    if (!patientIdQuery || otp.length !== 6) return;

    try {
      setVerifyOtpLoading(true);

      const res = await getEcgReportAction({
        patientId: patientIdQuery,
        viewOtherReport: true,
        otp, //  only here
      });

      const token =
        res?.patientSessionToken ||
        res?.data?.patientSessionToken ||
        res?.token;

      if (!token) {
        console.warn("Invalid OTP");
        return;
      }

      setPatientSessionToken(token);
      setReportToken?.(token);
      setShowConsentModal(false);

    } catch (err) {
      console.error("verify OTP failed", err);
    } finally {
      setVerifyOtpLoading(false);
    }
  }, [patientIdQuery, otp]);


 

  // no reports?
  const noReports = localEcgCount === 0;


  // helper to format interpretation label color
  const interpretationColor = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "normal") return "#348530";
    if (s === "critical") return "#D34848";
    if (s === "abnormal") return "#E4841A";
    return "#666";
  };

  // render a table row using MUI TableRow/TableCell
  const renderReportRow = (report, idx) => {
    const rid = report?.report?.id || "--";
    // add 5 hours ahead per request
    const taken = report?.report_timestamp
      ? moment(Number(report.report_timestamp)).format("DD/MM/YYYY, hh:mm a")
      : null;
    const interpreted = report?.interpretedAt ? moment(parseInt(report.interpretedAt)).format("DD/MM/YYYY, hh:mm a") : null;
    const interpretationStatus = report?.interpretation_report_status || "";

    return (
      <TableRow
        key={rid || `r-${idx}`}
        sx={{
          backgroundColor: "var(--app-surface)",
          boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
          overflow: "hidden",
          // body row cells - ensure they don't draw borders and follow rounded corners
          "& .MuiTableCell-root": {
            borderBottom: "none",
            py: 2,
            verticalAlign: "middle",
            backgroundColor: "transparent",
          },
          "&:last-child td, &:last-child th": { border: 0 },
          "&:hover": {
            backgroundColor: "#fbfbff",
          },
        }}
      >
        <TableCell
          onClick={() => navigate(`/ecg-reports/${report?.report?.id}`)}
          sx={{
            cursor: "pointer",
            textDecoration: "underline",
            color: "var(--app-text)",
            width: "20%",
            whiteSpace: "nowrap",
          }}
        >
          {rid}
        </TableCell>

        <TableCell sx={{ width: "18%", fontWeight: 500, fontSize: "15px", color: "var(--app-text)" }}>
           <TypeIcon type={report?.centerType} size={15} /> {report?.centerName || "--"}
        </TableCell>

        <TableCell sx={{ width: "18%", fontWeight: 500, fontSize: "15px", color: "var(--app-text)" }}>
          {taken ? taken : "--"}
        </TableCell>

        <TableCell sx={{ width: "22%", fontWeight: 500, fontSize: "15px", color: "var(--app-text)" }}>
          {interpreted ? interpreted : "--"}
        </TableCell>

        <TableCell sx={{ width: "22%", fontWeight: 500, fontSize: "15px" }}>
          <Typography sx={{ color: interpretationColor(interpretationStatus), fontWeight: 600 }}>
            {interpretationStatus ? interpretationStatus.charAt(0).toUpperCase() + interpretationStatus.slice(1) : "-"}
          </Typography>
        </TableCell>
      </TableRow>
    );
  };

  // --- Custom OTP inputs implementation (6 digits) ---
  const OTP_LENGTH = 6;
  const otpRefs = useRef([]);
  // ensure refs length
  otpRefs.current = Array.from({ length: OTP_LENGTH }).map((_, i) => otpRefs.current[i] || React.createRef());

  const handleOtpChangeSingle = (value, index) => {
    const digit = (value || "").replace(/\D/g, "").slice(0, 1);
    const arr = (otp || "").split("").slice(0, OTP_LENGTH);
    // fill missing with empty strings
    while (arr.length < OTP_LENGTH) arr.push("");
    arr[index] = digit;
    const joined = arr.join("").slice(0, OTP_LENGTH);
    setOtp(joined);
    if (digit && index < OTP_LENGTH - 1) {
      const next = otpRefs.current[index + 1];
      next?.current?.focus?.();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const arr = (otp || "").split("").slice(0, OTP_LENGTH);
      while (arr.length < OTP_LENGTH) arr.push("");
      if (arr[index]) {
        // clear current
        arr[index] = "";
        setOtp(arr.join(""));
        // keep focus here
      } else if (index > 0) {
        // move to prev and clear it
        const prev = otpRefs.current[index - 1];
        prev?.current?.focus?.();
        arr[index - 1] = "";
        setOtp(arr.join(""));
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      otpRefs.current[index - 1]?.current?.focus?.();
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.current?.focus?.();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = (e.clipboardData.getData("Text") || "").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    const fill = pasted.split("");
    while (fill.length < OTP_LENGTH) fill.push("");
    setOtp(fill.join("").slice(0, OTP_LENGTH));
    const lastIndex = Math.min(pasted.length - 1, OTP_LENGTH - 1);
    setTimeout(() => otpRefs.current[lastIndex]?.current?.focus?.(), 0);
  };

  // Helper lists for rendering: use ecgReports for local; other reports from data.otherReports
  const otherReportsList = Array.isArray(data?.otherReports) ? data.otherReports : [];
  const localList = Array.isArray(ecgReports) ? ecgReports : [];

  return (
    <>
      <Helmet>
        <title>Spandan - Patient Reports</title>
      </Helmet>

      <Box sx={{ width: "100%" }}>
        {/* Table header style + container */}
        <TableContainer className="app-table-card"
          component={Paper}
          elevation={0}
          sx={{
            backgroundColor: "transparent",
            boxShadow: "none",
            // use borderSpacing to create gaps between rows and rounded look
            "& table": {
              borderCollapse: "separate",
              borderSpacing: "0 14px",
            },
          }}
        >
          <Table>
            {!noReports && (
              <TableHead>
                {/* Header Row — give it matching 8px radius and hide overflow so corners show */}
                <TableRow
                  sx={{
                    height: 56,
                    borderRadius: "8px",
                    overflow: "hidden", // ensures rounded corners are visible

                    // ensure header cells don't show bottom border
                    "& .MuiTableCell-root": {
                      borderBottom: "none",
                      py: 2,
                      fontSize: 15,
                      fontWeight: 700,
                      fontFamily: "Figtree",
                      color: "var(--app-text-muted)",
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <TableCell>REPORT ID</TableCell>
                  <TableCell>CENTRE</TableCell>
                  <TableCell>TAKEN ON</TableCell>
                  <TableCell>INTERPRETED ON</TableCell>
                  <TableCell>INTERPRETATION</TableCell>
                </TableRow>
              </TableHead>
            )}

            <TableBody>
              {!noReports ? (
                <>
                  {localList.map((r, idx) => renderReportRow(r, idx))}

                  {otherReportsList.map((r, idx) => renderReportRow(r, idx))}
                </>
              ) : (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box
                      sx={{
                        height: "50vh",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                        my: 4,
                      }}
                    >
                      <img src={nopreview} alt="no-preview" style={{ width: 150 }} />
                      <Typography variant="h6" sx={{ mt: 2 }}>
                        There are no ECG reports taken in this organisation
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Bottom info + button */}
        {/* {(otherReportsCount > 0 && !data?.otherReports?.length) && (
          <Box sx={{ mt: 3, px: 1 }}>
            <Typography sx={{ color: "var(--app-text-muted)", mb: 2 }}>
              There are <strong>{otherReportsCount} more reports</strong> from other organisations
            </Typography>
            <Button
              variant="contained"
              onClick={handleOpenConsent}
              sx={{
                backgroundColor: "#5751E9",
                color: "#fff",
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                py: 1,
                "&:hover": { backgroundColor: "#3f3acb" },
              }}
            >
              Fetch Reports
            </Button>
          </Box>
        )} */}
      </Box>

      {/* Consent / OTP Dialog */}
      <Dialog
        open={showConsentModal}
        onClose={handleCloseConsent}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            p: 3,
          },
        }}
      >
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 700,
              fontFamily: "Figtree",
            }}
          >
            Fetch Reports
          </Typography>

          <IconButton onClick={handleCloseConsent}>
            <Icon icon="ic:round-close" width="22" height="22" />
          </IconButton>
        </Box>

        {/* Content */}
        <Box>
          {!sendOTP ? (
            <>
              <Typography
                sx={{
                  color: "var(--app-text-muted)",
                  fontSize: "15px",
                  mb: 3,
                  lineHeight: 1.6,
                }}
              >
                To fetch reports from other organisations, you need to take
                consent from the user through their mobile number{" "}
                <strong>{patientPhone}</strong> via OTP
              </Typography>

              <Button
                variant="contained"
                fullWidth
                onClick={handleSendOtp}
                disabled={sendOtpLoading}
                sx={{
                  height: "48px",
                  fontSize: "16px",
                  fontWeight: 600,
                  borderRadius: "10px",
                  textTransform: "none",
                  backgroundColor: "#5751E9",
                  "&:hover": {
                    backgroundColor: "#4c46d6",
                  },
                }}
              >
                {sendOtpLoading ? "Sending OTP..." : "Send OTP"}
              </Button>
            </>
          ) : (
            <>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                Verify OTP
              </Typography>

              <Typography
                sx={{
                  color: "var(--app-text-muted)",
                  fontSize: "14px",
                  mb: 3,
                }}
              >
                Please enter the OTP sent to <strong>{patientPhone}</strong>
              </Typography>

              {/* OTP Inputs */}
              <Box display="flex" justifyContent="center" mb={3}>
                <Box
                  component="form"
                  onPaste={handleOtpPaste}
                  sx={{ display: "flex", gap: 1.5 }}
                >
                  {Array.from({ length: OTP_LENGTH }).map((_, idx) => {
                    const val = (otp || "").charAt(idx) || "";
                    return (
                      <input
                        key={idx}
                        ref={otpRefs.current[idx]}
                        value={val}
                        onChange={(e) =>
                          handleOtpChangeSingle(e.target.value, idx)
                        }
                        onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                        maxLength={1}
                        inputMode="numeric"
                        type="text"
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: 10,
                          border: "1px solid var(--app-border)",
                          fontSize: 18,
                          textAlign: "center",
                          outline: "none",
                        }}
                        autoFocus={idx === 0}
                      />
                    );
                  })}
                </Box>
              </Box>

              {/* Timer */}
              <Box mb={3}>
                <Timer
                  seconds={59}
                  minutes={0}
                  resend={handleSendOtp}
                  text="Resend OTP in"
                  buttonColor="#5751E9"
                  background="var(--app-surface)"
                  ButtonText="Didn't get the code? Resend OTP"
                />
              </Box>

              <Button
                variant="contained"
                fullWidth
                onClick={handleVerifyOtp}
                disabled={
                  verifyOtpLoading || (otp || "").length !== OTP_LENGTH
                }
                sx={{
                  height: "48px",
                  fontSize: "16px",
                  fontWeight: 600,
                  borderRadius: "10px",
                  textTransform: "none",
                  backgroundColor: "#5751E9",
                  "&:hover": {
                    backgroundColor: "#4c46d6",
                  },
                }}
              >
                {verifyOtpLoading ? "Processing..." : "Verify and Fetch"}
              </Button>
            </>
          )}
        </Box>
      </Dialog>

    </>
  );
};

export default PatientECGReports;
